import { Component, OnDestroy, OnInit } from "@angular/core";
import { SubscriptionService } from "../../../subscription/subscription.service";
import { ActivatedRoute, Router } from "@angular/router";
import { BuyService } from "../../../buy/buy.service";
import { Model } from "../../../app.model";
import { HttpClient } from "@angular/common/http";
import { CompanyService } from "../../../company/company.service";
import { firstValueFrom } from "rxjs";
import { PaymentDetails, SaleOrder } from "../../../buy/buy.model";
import { observable } from "@myshared/mobx-connector";
import { runInAction } from 'mobx';
import { WorkflowCspService } from "../workflow-csp.service";
import { BaseWorkflowModel, WorkflowCspComponent } from "../workflow-csp.component";
import { AvailabilityProduct, AvailabilityResponse } from "../../workflow.types";

type WorkflowModel = BaseWorkflowModel & {
    availability: string;
    availabilityLevels: AvailabilityProduct[];
}

@Component({
    selector: 'app-workflow-csp-one-availability',
    templateUrl: './workflow-csp-one-availability.component.html',
    standalone: false
})
export class WorkflowCspOneAvailabilityComponent extends WorkflowCspComponent<WorkflowModel> implements OnInit, OnDestroy {

    @observable
    public model: WorkflowModel;

    constructor(protected route: ActivatedRoute,
        protected router: Router,
        protected subscriptionService: SubscriptionService,
        protected buyService: BuyService,
        protected m: Model,
        protected http: HttpClient,
        protected workflowCspService: WorkflowCspService,
        protected companyService: CompanyService) {
        super(route, router, subscriptionService, buyService, m, http, workflowCspService, companyService);
    }

    async ngOnInit() {
        await super.ngOnInit();

        if (!this.checkSubscriptionValid) {
            this.resetWorkflow();
            return;
        }

        if (this.availabilityOrder) {
            this.resetWorkflow();
            return;
        }

        if (this.model.subscription.isAvailabilityBeta) {
            this.step = 'contact-sales';
            this.navigate(['contact-sales']);
            return;
        }

        const result = await firstValueFrom(
            this.http.get<AvailabilityResponse>('/services/products/cloud')
        );

        runInAction(() => {
            this.model.availabilityLevels = (result.availability || []).map(t => ({ name: t.name, list_price: t.list_price }));
            this.model.availabilityLevels.push({ name: 'STANDARD', list_price: 0 });
            this.model.availability = this.model.subscription.availability;
        });

        this.loadCompany();

        this.initialized = true;
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    public onInvoiceAddressBack() {
        this.navigate(['availability']);

    }

    get workflowName(): string {
        return 'availability';
    }

    public onPremiumSelected() {
        this.navigate(['address']);
    }
    
    public onStandardSelected() {
        this.navigate(['downgrade']);
    }

    public onAvailabilityBack() {
        this.resetWorkflow();
    }

    public async onDowngradeSelected() {
        const saleOrder = await firstValueFrom(this.workflowCspService.oneApplianceDowngradePremium(this.applianceId))
        runInAction(() => {
            this.model.saleOrder = saleOrder;
            this.m.buy.currentOrder = saleOrder;
        });
        
        await firstValueFrom(this.buyService.confirmUpgrade());
        this.navigate(['complete']);
    }

    onDowngradeBack() {
        this.resetWorkflow();
    }

    public async onInvoiceNext(e: any) {
        if (this.isPaymentStepNeeded) {
            this.navigate(['payment']);
            return;
        }

        await this.checkout();
    }

    protected onSummaryBack() {
        if (this.isPaymentStepNeeded) {
            this.navigate(['payment']);
            return;
        }

        this.onPaymentMethodBack();
    }

    private async checkout() {
        await this.createOffer();
        await firstValueFrom(this.buyService.getOfferRecalculate(this.model.saleOrder.name));
        this.navigate(['checkout']);
    }

    private async createOffer() {
        if (this.model.saleOrder?.name) return;

        const saleOrder = await firstValueFrom(this.workflowCspService.oneApplianceAddPremium(this.applianceId));
        runInAction(() => {
            this.model.saleOrder = saleOrder;
        })
    }

    public onComplete() {
        this.resetWorkflow();
    }
    
    public async onPaymentMethod(payment: PaymentDetails) {
        await this.setPaymentDetails(payment);
        await this.checkout();
    }

    public onPaymentMethodBack() {
        this.navigate(['address']);
    }

    public onBuy() {
        this.buyService.confirmUpgrade().subscribe(
            (_: SaleOrder) => {
                this.navigate(['complete']);
            }
        );
    }

    private get availabilityOrder(): SaleOrder {
        return this.model.subscription.orders.find(order =>
            order.lines.find(line =>
                ['PC-ONE-PREMIUM', 'PC-ONE-CANCEL-PREMIUM'].includes(line.product)
            )
        );
    }
}
